import type { StateCreator } from "zustand";
import type { CampaignKey } from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";

/** One prize of a won-notification: just the stable per-drop key echoed back in the per-prize
 *  ack (`ack:notifications`' `prizes` payload).
 *
 *  No title — since #3922 the pre-reveal payload carries only this opaque key, and the identity
 *  arrives on a `prize_drop_revealed` push once the ack lands (see `disclosedTitles`). */
export interface QueuedWonPrize {
  dropKey: string;
}

/** A prize the player has now revealed: what the pre-reveal payload withheld. Keyed by
 *  `dropKey`, which is globally unique, so the join needs nothing else — and must not be keyed
 *  by notification id, because the won row is already deleted by the time this arrives. */
/** The two kinds of bet feedback a campaign can carry (`bet_too_low` / `bet_bonus_excluded`). */
export type BetFeedbackKind = "bet_too_low" | "bet_bonus_excluded";

export interface RevealedPrize {
  title: string;
  games: string[];
}

/** One `{notificationId, dropKeys}` pair as sent in an `ack:notifications` `prizes` entry —
 *  kept verbatim so a retry re-sends exactly what the first attempt claimed. */
export interface RevealAckTarget {
  notificationId: number;
  dropKeys: string[];
}

/**
 * A reveal the player has committed to, per campaign. Claiming is a real round trip that must
 * succeed before anything is disclosed, so the surfaces render from this rather than from the
 * `unrevealedWins` seam (which stays occupied for the whole batch — see the service layer).
 *
 * `count` is a SNAPSHOT taken when the claim is sent. Never recompute it from the live queue:
 * `usePendingWinCount` floors at 1 and grows if a new win lands mid-batch, so "all N credited"
 * would go wrong in both directions.
 */
export type RevealBatch =
  | { status: "claiming"; count: number; targets: RevealAckTarget[] }
  | {
      status: "revealed";
      count: number;
      targets: RevealAckTarget[];
      /** The `prize_drop_revealed` rows this batch rendered from. Acked on dismiss rather than
       *  on receipt, so a reconnect mid-reveal replays them and the batch — which lives on in
       *  this store across a dropped socket — settles again. It survives a reconnect, NOT a
       *  reload: the batch itself is in-memory, so a disclosure replayed with nothing waiting
       *  for it has no one to ack it, which is why the backend gives the row a TTL. */
      disclosureIds: number[];
    }
  | { status: "failed"; count: number; targets: RevealAckTarget[] };

/**
 * One `prize_drop_won` notification waiting to be revealed. `prizes` holds the
 * ones not yet confirmed — the head is the one currently offered for reveal;
 * each confirm acks its prize individually (the backend deletes the row with
 * its last prize), so partial progress survives reloads.
 */
export interface QueuedWonNotification {
  notificationId: number;
  /** The wire's numeric campaign id — kept so the key can be re-resolved when
   *  the campaigns page lands after the notification (see rekeyWonNotifications). */
  campaignId: number;
  campaignKey: CampaignKey;
  prizes: QueuedWonPrize[];
}

/**
 * The `notifications` stream slice. Only `prize_drop_won` items are held here
 * (everything else is acked on receipt by the handler and never stored). Wins
 * queue per campaign and reveal ONE prize at a time through the existing
 * `unrevealedWins` seam; un-acked notifications are re-delivered by the backend
 * on resubscribe, so `seenNotificationIds` dedupes re-deliveries.
 */
export interface NotificationsSlice {
  wonQueueByCampaign: Record<CampaignKey, QueuedWonNotification[]>;
  /** Every prize already queued or acked this session, keyed
   *  `<notificationId>:<dropKey>` — per PRIZE (not per notification) so a
   *  merged row re-delivered with newly accumulated prizes still queues the
   *  new ones while re-deliveries of known ones stay deduped. */
  seenPrizeKeys: Record<string, true>;
  /** Ids of every notification already *displayed* this session (mission
   *  toasts). Acks are fire-and-forget for those types, so an ack whose
   *  response died with the socket leaves the row pending server-side and it
   *  re-arrives on resubscribe — without this it would toast twice. */
  seenNotificationIds: Record<number, true>;
  /** Ack ids (whole notifications) not yet confirmed delivered by the
   *  backend — kept until the stream answers OK so acks survive a
   *  mid-reconnect send (see flushPendingAcks in services/notifications). */
  pendingAckIds: Record<number, true>;
  /** Per-prize acks awaiting delivery confirmation, `notificationId → set of
   *  drop keys` — same lifecycle as `pendingAckIds`. */
  pendingAckPrizes: Record<number, Record<string, true>>;
  /** Prize identities disclosed after a confirmed reveal, keyed by `dropKey` (#3922). Written
   *  unconditionally on every `prize_drop_revealed` push — the single reveal and the bulk tally
   *  both render from here, and a disclosure replayed after a reload must land somewhere. */
  disclosedTitles: Record<string, RevealedPrize>;
  /** The in-flight / settled reveal per campaign. */
  revealBatchByCampaign: Record<CampaignKey, RevealBatch>;
  /** Campaigns (numeric id) with news the player hasn't looked at yet: a win queued for reveal or
   *  a toast they were shown. Event-based, not state: it clears when the campaign is viewed even
   *  if its win is still waiting there. The overlay's rail shows it as a dot. */
  campaignNews: Record<number, true>;
  /** Bet feedback pending per campaign (numeric id) — the legacy overlay's bars: the player's
   *  recent bets are too low, or bonus money on a real-cash-only campaign. State the backend sets
   *  and cancels, so each push replaces it whole. */
  betFeedback: Record<number, BetFeedbackKind[]>;
  notificationsStream: CampaignStreamState;

  /** Queue new won-notifications (already deduped against `seenPrizeKeys`
   *  by the caller via `markPrizesSeen`). */
  enqueueWonNotifications: (items: QueuedWonNotification[]) => void;
  markPrizesSeen: (keys: string[]) => void;
  markNotificationsSeen: (ids: number[]) => void;
  noteCampaignNews: (campaignIds: number[]) => void;
  clearCampaignNews: (campaignId: number) => void;
  setBetFeedback: (feedback: Record<number, BetFeedbackKind[]>) => void;
  /**
   * Confirm the head prize for a campaign: drops it from the head notification,
   * removing the notification when it has no prizes left. Pure state change —
   * the caller reads the returned info to send the ack and re-project the seam.
   */
  advanceWonQueue: (key: CampaignKey) => void;
  /** Re-home a queue whose campaign key was resolved from a stale/absent
   *  campaigns page (appends to any queue already at `toKey`). */
  moveWonQueue: (fromKey: CampaignKey, toKey: CampaignKey) => void;
  /** Drop a whole campaign queue (orphaned — see ackOrphanedWonQueues). */
  dropWonQueue: (key: CampaignKey) => void;
  /** Drop any queued prize whose `${notificationId}:${dropKey}` isn't in `validKeys` — the
   *  backend's own current set (see pruneAckedWonPrizes). */
  pruneAckedWonPrizes: (validKeys: Set<string>) => void;
  queueAckIds: (ids: number[]) => void;
  clearAckIds: (ids: number[]) => void;
  queueAckPrize: (notificationId: number, dropKey: string) => void;
  clearAckPrizes: (prizes: Record<number, string[]>) => void;
  /** Record disclosed identities from a `prize_drop_revealed` push. */
  setDisclosedTitles: (entries: Record<string, RevealedPrize>) => void;
  /** Drop every prize of `targets` from the campaign's queue in one write (a bulk
   *  `advanceWonQueue`) — the whole batch leaves the queue together. */
  drainWonQueue: (key: CampaignKey, targets: RevealAckTarget[]) => void;
  setRevealBatch: (key: CampaignKey, batch: RevealBatch) => void;
  clearRevealBatch: (key: CampaignKey) => void;
  setNotificationsSubscribed: (subscribed: boolean) => void;
  setNotificationsLoading: (loading: boolean) => void;
  clearNotifications: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createNotificationsSlice: StateCreator<NotificationsSlice> = (
  set
) => ({
  wonQueueByCampaign: {},
  seenPrizeKeys: {},
  seenNotificationIds: {},
  pendingAckIds: {},
  pendingAckPrizes: {},
  disclosedTitles: {},
  revealBatchByCampaign: {},
  campaignNews: {},
  betFeedback: {},
  notificationsStream: initialStream,

  enqueueWonNotifications: (items) =>
    set((state) => {
      if (items.length === 0) return state;
      const queues = { ...state.wonQueueByCampaign };
      for (const item of items) {
        queues[item.campaignKey] = [...(queues[item.campaignKey] ?? []), item];
      }
      return { wonQueueByCampaign: queues };
    }),

  markPrizesSeen: (keys) =>
    set((state) => {
      if (keys.length === 0) return state;
      const seen = { ...state.seenPrizeKeys };
      for (const key of keys) seen[key] = true;
      return { seenPrizeKeys: seen };
    }),

  markNotificationsSeen: (ids) =>
    set((state) => {
      if (ids.length === 0) return state;
      const seen = { ...state.seenNotificationIds };
      for (const id of ids) seen[id] = true;
      return { seenNotificationIds: seen };
    }),

  noteCampaignNews: (campaignIds) =>
    set((state) => {
      const fresh = campaignIds.filter((id) => !(id in state.campaignNews));
      if (fresh.length === 0) return state;
      const news = { ...state.campaignNews };
      for (const id of fresh) news[id] = true;
      return { campaignNews: news };
    }),

  setBetFeedback: (betFeedback) => set({ betFeedback }),
  clearCampaignNews: (campaignId) =>
    set((state) => {
      if (!(campaignId in state.campaignNews)) return state;
      const news = { ...state.campaignNews };
      delete news[campaignId];
      return { campaignNews: news };
    }),

  advanceWonQueue: (key) =>
    set((state) => {
      const queue = state.wonQueueByCampaign[key];
      const head = queue?.[0];
      if (!head) return state;
      const remaining = head.prizes.slice(1);
      const nextQueue =
        remaining.length > 0
          ? [{ ...head, prizes: remaining }, ...queue.slice(1)]
          : queue.slice(1);
      const queues = { ...state.wonQueueByCampaign };
      if (nextQueue.length > 0) queues[key] = nextQueue;
      else delete queues[key];
      return { wonQueueByCampaign: queues };
    }),

  moveWonQueue: (fromKey, toKey) =>
    set((state) => {
      const moving = state.wonQueueByCampaign[fromKey];
      if (!moving || fromKey === toKey) return state;
      const queues = { ...state.wonQueueByCampaign };
      delete queues[fromKey];
      queues[toKey] = [
        ...(queues[toKey] ?? []),
        ...moving.map((item) => ({ ...item, campaignKey: toKey })),
      ];
      return { wonQueueByCampaign: queues };
    }),

  dropWonQueue: (key) =>
    set((state) => {
      if (!(key in state.wonQueueByCampaign)) return state;
      const queues = { ...state.wonQueueByCampaign };
      delete queues[key];
      return { wonQueueByCampaign: queues };
    }),

  pruneAckedWonPrizes: (validKeys) =>
    set((state) => {
      let changed = false;
      const queues: Record<CampaignKey, QueuedWonNotification[]> = {};
      for (const [key, items] of Object.entries(state.wonQueueByCampaign)) {
        const nextItems: QueuedWonNotification[] = [];
        for (const item of items) {
          const prizes = item.prizes.filter((p) =>
            validKeys.has(`${item.notificationId}:${p.dropKey}`)
          );
          if (prizes.length !== item.prizes.length) changed = true;
          if (prizes.length > 0) nextItems.push({ ...item, prizes });
        }
        if (nextItems.length > 0) queues[key] = nextItems;
        else if (items.length > 0) changed = true;
      }
      return changed ? { wonQueueByCampaign: queues } : state;
    }),

  queueAckIds: (ids) =>
    set((state) => {
      if (ids.length === 0) return state;
      const pending = { ...state.pendingAckIds };
      for (const id of ids) pending[id] = true;
      return { pendingAckIds: pending };
    }),

  clearAckIds: (ids) =>
    set((state) => {
      if (ids.length === 0) return state;
      const pending = { ...state.pendingAckIds };
      for (const id of ids) delete pending[id];
      return { pendingAckIds: pending };
    }),

  queueAckPrize: (notificationId, dropKey) =>
    set((state) => ({
      pendingAckPrizes: {
        ...state.pendingAckPrizes,
        [notificationId]: {
          ...state.pendingAckPrizes[notificationId],
          [dropKey]: true,
        },
      },
    })),

  clearAckPrizes: (prizes) =>
    set((state) => {
      const pending = { ...state.pendingAckPrizes };
      for (const [id, dropKeys] of Object.entries(prizes)) {
        const forId = { ...pending[Number(id)] };
        for (const key of dropKeys) delete forId[key];
        if (Object.keys(forId).length > 0) pending[Number(id)] = forId;
        else delete pending[Number(id)];
      }
      return { pendingAckPrizes: pending };
    }),

  setDisclosedTitles: (entries) =>
    set((state) =>
      Object.keys(entries).length === 0
        ? state
        : { disclosedTitles: { ...state.disclosedTitles, ...entries } }
    ),

  drainWonQueue: (key, targets) =>
    set((state) => {
      const queue = state.wonQueueByCampaign[key];
      if (!queue) return state;
      const draining = new Map(
        targets.map((t) => [t.notificationId, new Set(t.dropKeys)])
      );
      const nextQueue: QueuedWonNotification[] = [];
      for (const item of queue) {
        const drop = draining.get(item.notificationId);
        const prizes = drop
          ? item.prizes.filter((p) => !drop.has(p.dropKey))
          : item.prizes;
        if (prizes.length > 0) nextQueue.push({ ...item, prizes });
      }
      const queues = { ...state.wonQueueByCampaign };
      if (nextQueue.length > 0) queues[key] = nextQueue;
      else delete queues[key];
      return { wonQueueByCampaign: queues };
    }),

  setRevealBatch: (key, batch) =>
    set((state) => ({
      revealBatchByCampaign: { ...state.revealBatchByCampaign, [key]: batch },
    })),

  clearRevealBatch: (key) =>
    set((state) => {
      if (!(key in state.revealBatchByCampaign)) return state;
      const next = { ...state.revealBatchByCampaign };
      delete next[key];
      return { revealBatchByCampaign: next };
    }),

  setNotificationsSubscribed: (subscribed) =>
    set((state) => ({
      notificationsStream: {
        ...state.notificationsStream,
        subscribed,
        loading: false,
      },
    })),
  setNotificationsLoading: (loading) =>
    set((state) => ({
      notificationsStream: { ...state.notificationsStream, loading },
    })),

  // Logout: drop queues + flags. Seen-keys go too — a fresh login re-delivers
  // whatever is still un-acked, and those should queue again (including any
  // whose ack was pending but never confirmed delivered).
  clearNotifications: () =>
    set({
      wonQueueByCampaign: {},
      seenPrizeKeys: {},
      seenNotificationIds: {},
      pendingAckIds: {},
      pendingAckPrizes: {},
      disclosedTitles: {},
      revealBatchByCampaign: {},
      campaignNews: {},
      betFeedback: {},
      notificationsStream: initialStream,
    }),
});
